import { ChangeDetectionStrategy, Component, ElementRef, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable, catchError, concatMap, finalize, from, of, tap } from 'rxjs';

import { EventType } from '@app/shared/constants-server';
import type { Attach } from '@app/shared/_models/common';
import type { RentalPrescription } from '@app/shared/_models/sales';
import { AlertService } from '@app/shared/alert.service';
import { AttachService } from '@app/shared/attach/attach.service';
import { canOpenInline } from '@app/shared/attach/attach-inline';
import type { Day, RentalLineView } from '../rental-billing.model';
import { DecisionRunResult, PrescriptionFileRef, prescriptionRefsOf, serverMessage, tooManyDecisions } from '../rental-billing.service';
import { CLOSED_LINE, lineClosed } from '../rental-billing.labels';
import { RentalDecisionDialogsService } from '../rental-decision-dialogs.service';
import { PrescriptionEnd, RentalDecisionEvent, prescriptionAdd, prescriptionReplace } from '../rental-decision-events';
import type { PrescriptionDialogData } from '../rental-dialogs.contract';
import { addDays, firstOfMonth, frDay, frShort, frWeekday } from '../rental-days';
import { PickerMarks, pickerMarksOf } from '../rental-picker.geometry';
import { deliveredDay } from '../rental-planning.geometry';
import { readableLine } from '../planning/rental-planning.view';
import { PickerPhase, RentalDayPickerComponent } from '../day-picker/rental-day-picker.component';
import { RentalEffectComponent } from '../effect/rental-effect.component';
import { RentalDecisionDialogBase, cancelledText, lineName, monthShowing, stampText } from './rental-decision-dialog.base';

type Unit = 'DAY' | 'WEEK' | 'MONTH' | 'YEAR'; // YEAR : écran seul, envoyé en mois (le serveur ne connaît que DAY, WEEK, MONTH)
interface Entry { lineId: number; p: RentalPrescription; }
// Une ordonnance = ses entrées identiques (dates, jour d'ordonnance, référence) sur les lignes du dossier : une par matériel prescrit.
interface PrescriptionRow { key: string; entries: Entry[]; what: string; lines: string; extra: string; stamp: string; cancelled: string | null; replaces: number | null; live: boolean; files: Attach[]; }
interface RxCandidate { id: number; name: string; memo: string | null; covered: string; checked: boolean; }

// Quatre mois dès que la fenêtre les tient (dialogue à 1 260 px), trois dès 1 024 px (960 px), deux en dessous.
export function prescriptionMonths(): 2 | 3 | 4 {
  const w = typeof window !== 'undefined' ? window.innerWidth : 0;
  return w >= 1340 ? 4 : w >= 1024 ? 3 : 2;
}

export interface PrescriptionCoverage { count: Map<Day, number>; covered: Set<Day>; partly: Set<Day>; missing: Day | null; }

// Couverture des lignes cochées : covered = toutes couvertes ce jour-là, partly = une partie ; missing = le plus tôt des premiers jours sans ordonnance (dès la livraison, avant la reprise).
export function prescriptionCoverage(lines: RentalLineView[]): PrescriptionCoverage {
  const count = new Map<Day, number>();
  let missing: Day | null = null;
  for (const l of lines) {
    const live = (l.decisions?.prescriptions ?? []).filter((p) => !p.cancelled);
    const days = new Set<Day>();
    for (const p of live) for (let d = p.start; d <= p.end; d = addDays(d, 1)) days.add(d);
    for (const d of days) count.set(d, (count.get(d) ?? 0) + 1);
    const first = firstUncovered(l);
    if (first !== null && (missing === null || first < missing)) missing = first;
  }
  const covered = new Set<Day>();
  const partly = new Set<Day>();
  for (const [d, n] of count) (n === lines.length ? covered : partly).add(d);
  return { count, covered, partly, missing };
}

// Premier jour sans ordonnance d'une ligne : dès la livraison (à défaut sa première prescription), avant la reprise ; null s'il n'y en a pas.
export function firstUncovered(l: RentalLineView): Day | null {
  const live = (l.decisions?.prescriptions ?? []).filter((p) => !p.cancelled);
  const covered = (d: Day) => live.some((p) => p.start <= d && d <= p.end);
  let d: Day | null = deliveredDay(l) ?? live.map((p) => p.start).sort()[0] ?? null;
  while (d !== null && covered(d)) d = addDays(d, 1);
  const back = l.days?.find((r) => r.state === 'RETURNED')?.start ?? null;
  return d !== null && (back === null || d < back) ? d : null;
}

// Premier jour sans prescription : lendemain du dernier jour couvert par une prescription non annulée ; null sans prescription.
export function uncoveredFrom(list: RentalPrescription[]): Day | null {
  const ends = list.filter((p) => !p.cancelled).map((p) => p.end).sort();
  return ends.length ? addDays(ends[ends.length - 1], 1) : null;
}

// Lignes proposées à cocher : celles dont le contrat exige une prescription, et la ligne d'où l'on vient.
export function prescriptionCandidates(lines: RentalLineView[], anchor: number): RentalLineView[] {
  return lines.filter((l) => readableLine(l) && (l.params?.prescription_required === true || l.id === anchor));
}

// Fichiers d'une nouvelle ordonnance : rattachés aux numéros rendus par le serveur pour chaque matériel (decision_id), jamais devinés.
export function fileRefsOf(run: DecisionRunResult): PrescriptionFileRef[] {
  return run.done.flatMap((o) => (o.ok && o.decisionId > 0 ? [{ line_id: o.event.data.line_id, id: o.decisionId }] : []));
}

// Nom normalisé du fichier (JP 28/10) : ordonnance_<patient>_<n° du dossier>_<date de l'ordonnance>_<n° du prescripteur>.<ext>, _2, _3… pour les suivants.
export function prescriptionFileName(o: { patient: string | null; orderId: number; prescribedOn: Day | null; prescriber: string | null }, original: string, rank = 1): string {
  const slug = (s: string | null | undefined) => (s ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  const digits = (o.prescriber ?? '').replace(/\D/g, '');
  const dot = original.lastIndexOf('.');
  const ext = dot > 0 ? original.slice(dot).toLowerCase().replace(/[^.a-z0-9]/g, '') : '';
  const parts = ['ordonnance', slug(o.patient) || 'patient', String(o.orderId), o.prescribedOn ?? 'sans-date', digits || slug(o.prescriber) || 'sans-prescripteur'];
  return parts.join('_') + (rank > 1 ? `_${rank}` : '') + ext;
}

const entryKey = (p: RentalPrescription): string => (p.cancelled ? 'c' : 'v') + JSON.stringify([p.start, p.end, p.prescribed_on ?? null, p.reference?.trim() || null]);
const NO_LINE = 'Cochez au moins un matériel prescrit.';
const NO_FILE = 'Joignez le fichier de l\'ordonnance (obligatoire).';
const NO_DATE = 'Saisissez la date de l\'ordonnance (obligatoire).';
const NO_PRESCRIBER = 'Saisissez le n° du prescripteur (obligatoire).';
const REPLACE_REASON = 'Motif du remplacement obligatoire.';
const UNITS: { value: Unit; label: string }[] = [
  { value: 'DAY', label: 'Jours' },
  { value: 'WEEK', label: 'Semaines' },
  { value: 'MONTH', label: 'Mois' },
  { value: 'YEAR', label: 'Années' },
];

function unitText(n: number, u: Unit): string {
  if (u === 'MONTH') return `${n} mois`;
  if (u === 'YEAR') return `${n} an${n > 1 ? 's' : ''}`;
  return `${n} ${u === 'DAY' ? 'jour' : 'semaine'}${n > 1 ? 's' : ''}`;
}

// Prescriptions du dossier : une ordonnance couvre les matériels cochés, un évènement 2105 par ligne (ajout ou remplacement), du start au end inclus ; le end d'une durée est rendu par le serveur (E.7).
@Component({
  selector: 'app-rental-prescription-dialog',
  templateUrl: './rental-prescription-dialog.component.html',
  styleUrls: ['./rental-prescription-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatDialogModule, MatIconModule, MatProgressSpinnerModule, MatSelectModule, MatTooltipModule, RentalDayPickerComponent, RentalEffectComponent],
})
export class RentalPrescriptionDialogComponent extends RentalDecisionDialogBase<PrescriptionDialogData> {
  protected readonly dialogs = inject(RentalDecisionDialogsService);
  private readonly alert = inject(AlertService);
  private readonly attach = inject(AttachService);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  readonly units = UNITS;
  readonly months = prescriptionMonths();
  allOn = false; // case de tête : tous les matériels cochés
  someOn = false; // case de tête indéterminée (« − ») : une partie seulement
  files: Attach[] = []; // fichiers d'ordonnance du dossier (pièces du dossier marquées)
  pending: File[] = []; // joints à la nouvelle prescription, envoyés après son enregistrement
  attaching: string | null = null; // ordonnance dont un fichier part
  filesError: string | null = null;

  selected: number[] = []; // lignes cochées, dans l'ordre du dossier
  replacing: Map<number, number> | null = null; // ligne → entrée remplacée ; null = nouvelle prescription
  replaceKey: string | null = null;
  start: Day | null = null; // premier jour prescrit
  end: Day | null = null; // dernier jour couvert, inclus
  endMode: 'end' | 'duration' = 'duration'; // fin saisie en durée par défaut (JP 29/10), ou en dernier jour (end)
  durationText = '';
  unit: Unit = 'MONTH';
  prescribedOn: Day | null = null;
  reference = '';
  replaceReason = '';
  leftMonth: Day | null = null;
  // Ce que pose un clic dans le calendrier : la date de l'ordonnance (cadre et anneau bleus) ou le premier jour (cadre et disque orange) ; suit le dernier champ pris (JP 28/10).
  pickTarget: 'ordonnance' | 'start' = 'start';
  // Remplacement : les dates d'origine sont posées, le premier clic après le début déplace la fin ; null = phase tenue par le calendrier.
  pickPhase: PickerPhase | null = null;

  line: RentalLineView | null = null; // ligne d'où l'on vient
  candidates: RxCandidate[] = [];
  name = '';
  rows: PrescriptionRow[] = [];
  required = false;
  blocked: string | null = null;
  marks: PickerMarks | null = null;
  startValue = '';
  endValue = '';
  prescribedValue = '';
  errors: string[] = [];
  focusIds: number[] = [];
  scope: string | null = null;
  canSave = false;
  private seeded = false;

  constructor() {
    super();
    this.update();
    this.loadFiles();
  }

  // Fichier exigé pour une nouvelle ordonnance ; un remplacement garde celui de l'ordonnance remplacée s'il y en a un (JP 28/10).
  get fileRequired(): boolean {
    if (this.pending.length) return false;
    return !this.replaceKey || !this.rows.find((r) => r.key === this.replaceKey)?.files.length;
  }

  get fileHelp(): string {
    return this.fileRequired ? 'PDF ou image, envoyé à l\'enregistrement.' : 'Le fichier de l\'ordonnance remplacée reste joint.';
  }

  get formTitle(): string {
    return this.replacing ? 'Remplacer la prescription' : 'Nouvelle prescription';
  }

  // Le end d'une durée n'est jamais calculé ici : il est lu dans la simulation (after_decisions, déjà en jours), si elle répond à la saisie en cours.
  get serverEnd(): Day | null {
    const evs = this.events();
    if (!evs.length || !this.simAnswers(evs)) return null;
    const v = this.sim.view;
    const sl = v?.lines.find((x) => x.line_id === this.selected[0]);
    const p = sl?.after_decisions.prescriptions.find((x) => x.id === v?.decision_ids[0]);
    return p && p.start === this.start ? p.end : null;
  }

  get rangeText(): string {
    const s = this.start;
    if (this.pickTarget === 'ordonnance' && !this.prescribedOn) return 'Cliquez la date de l\'ordonnance (anneau bleu), puis le premier jour prescrit (disque orange).';
    if (!s) return this.endMode === 'end' ? 'Cliquez le premier jour prescrit, puis le dernier jour couvert.' : 'Cliquez le premier jour prescrit, puis saisissez la durée.';
    if (this.endMode === 'end') return this.end ? `Prescription du ${frWeekday(s)} au ${frWeekday(this.end)} inclus` : `Prescription à partir du ${frWeekday(s)} : cliquez le dernier jour couvert.`;
    const n = this.duration();
    if (n === null) return `Prescription à partir du ${frWeekday(s)} : saisissez la durée.`;
    const last = this.serverEnd;
    return `Prescription du ${frWeekday(s)} pour ${unitText(n, this.unit)}` + (last ? ` · Dernier jour couvert : ${frDay(last)} (calculé par le serveur)` : '');
  }

  protected update(): void {
    const view = this.store.view;
    const l = this.store.line(this.data.lineId) ?? null;
    this.line = l;
    if (!view || !l) {
      this.canSave = false;
      this.plan(null);
      return;
    }
    const lines = prescriptionCandidates(this.store.rentalLines(), l.id);
    if (!this.seeded) {
      this.seeded = true;
      this.selected = (this.data.lineIds ?? [l.id]).filter((id) => lines.some((x) => x.id === id));
      if (this.data.endMode) this.endMode = this.data.endMode;
      if (!this.data.replaceId) this.pickTarget = 'ordonnance'; // une saisie neuve commence par la date de l'ordonnance, posée au-dessus
      if (this.data.replaceId) this.useReplace(this.keyOf(l.id, this.data.replaceId));
    }
    this.rows = this.rowsOf(lines);
    if (this.replaceKey !== null && !this.rows.some((r) => r.key === this.replaceKey && r.live)) this.stopReplace();
    const chosen = lines.filter((x) => this.selected.includes(x.id));
    this.selected = chosen.map((x) => x.id);
    const scopeLines = chosen.length ? chosen : lines;
    const firstFree = scopeLines.map((x) => firstUncovered(x) ?? uncoveredFrom(x.decisions?.prescriptions ?? []) ?? deliveredDay(x)).filter((d): d is Day => !!d).sort()[0];
    this.leftMonth ??= firstOfMonth(this.start ?? firstFree ?? view.as_of); // le mois du premier jour sans ordonnance, trou du début compris (JP 29/10)
    this.candidates = lines.map((x) => this.candidateOf(x));
    this.allOn = lines.length > 0 && chosen.length === lines.length;
    this.someOn = chosen.length > 0 && !this.allOn;
    this.name = chosen.length ? chosen.map(lineName).join(' + ') : `Dossier n° ${this.data.orderId}`;
    this.required = lines.some((x) => x.params?.prescription_required === true);
    this.blocked = l.id <= 0 ? 'Ligne pas encore enregistrée : aucune décision possible.' : l.lifecycle_unreadable || !l.decisions ? (l.anomalies[0] ?? 'Cycle de vie illisible') : lineClosed(l) ? CLOSED_LINE : null;
    this.marks = this.coverageMarks(chosen); // JP 28/10 : la couverture des seuls matériels cochés, rien si aucun
    this.startValue = this.start ? frDay(this.start) : '';
    this.endValue = this.end ? frDay(this.end) : '';
    this.prescribedValue = this.prescribedOn ? frDay(this.prescribedOn) : '';
    this.focusIds = [...this.selected];
    this.scope = `· ${this.name}`;
    this.errors = this.errorsOf();
    const evs = this.blocked ? [] : this.events();
    const many = tooManyDecisions(evs.length);
    if (many) this.errors.push(many);
    this.canSave = !this.saving && evs.length > 0 && this.errors.length === 0;
    this.plan(evs.length && !this.errors.some((e) => e !== REPLACE_REASON) ? evs : null);
  }

  // Trait plein : tous les cochés couverts ; pointillé : une partie ; cercle rouge : le premier jour manquant d'au moins un.
  private coverageMarks(chosen: RentalLineView[]): PickerMarks {
    const base = pickerMarksOf(chosen, { kind: 'prescription' });
    const cov = prescriptionCoverage(chosen);
    const n = chosen.length;
    return {
      ...base, covered: cov.covered, partly: cov.partly, missing: cov.missing,
      tip: (d: Day): string => {
        const k = cov.count.get(d) ?? 0;
        const said = [d === cov.missing ? 'premier jour sans ordonnance' : null, k === 0 ? null : k === n ? 'couvert par ordonnance' : `${k} matériel${k > 1 ? 's' : ''} sur ${n} couvert${k > 1 ? 's' : ''}`];
        return [base.tip(d), ...said.filter((x): x is string => !!x)].join(' · ');
      },
    };
  }

  private candidateOf(x: RentalLineView): RxCandidate {
    const live = (x.decisions?.prescriptions ?? []).filter((p) => !p.cancelled);
    const free = uncoveredFrom(live);
    const hole = firstUncovered(x); // un jour sans ordonnance avant la dernière fin : nommé, la couverture n'est pas continue (JP 29/10)
    const last = free ? frDay(addDays(free, -1)) : '';
    return {
      id: x.id, name: lineName(x), memo: x.memo?.trim() || null, checked: this.selected.includes(x.id),
      covered: !free ? 'aucune prescription' : hole !== null && hole < free ? `${frShort(hole)} sans ordonnance · couvert du ${frDay(live.map((p) => p.start).sort()[0])} au ${last}` : `couvert jusqu'au ${last}`,
    };
  }

  // Entrées identiques des lignes candidates réunies en une ordonnance ; tri par premier jour.
  private rowsOf(lines: RentalLineView[]): PrescriptionRow[] {
    const groups = new Map<string, Entry[]>();
    for (const x of lines) for (const p of x.decisions?.prescriptions ?? []) {
      const k = entryKey(p);
      groups.set(k, [...(groups.get(k) ?? []), { lineId: x.id, p }]);
    }
    const names = new Map(lines.map((x) => [x.id, lineName(x)]));
    return [...groups.entries()]
      .sort((a, b) => a[1][0].p.start.localeCompare(b[1][0].p.start) || a[0].localeCompare(b[0]))
      .map(([key, entries]) => {
        const p = entries[0].p;
        const extra = [p.prescribed_on ? `ordonnance du ${frDay(p.prescribed_on)}` : null, p.reference?.trim() ? `réf. ${p.reference.trim()}` : null].filter((s) => !!s).join(' · ');
        const replaces = entries.every((e) => e.p.replaces != null && e.p.replaces === p.replaces) ? p.replaces ?? null : null;
        return {
          key, entries, what: `du ${frDay(p.start)} au ${frDay(p.end)} inclus`, lines: entries.map((e) => names.get(e.lineId) ?? `ligne ${e.lineId}`).join(' · '),
          extra, stamp: stampText(this.store, p.created), cancelled: p.cancelled ? cancelledText(this.store, p.cancelled) : null, replaces, live: !p.cancelled,
          files: this.files.filter((a) => prescriptionRefsOf(a).some((r) => entries.some((e) => e.lineId === r.line_id && this.lineage(e).includes(r.id)))),
        };
      });
  }

  // L'entrée et celles qu'elle remplace, de proche en proche : le fichier d'une ordonnance suit son remplacement.
  private lineage(e: Entry): number[] {
    const list = this.store.line(e.lineId)?.decisions?.prescriptions ?? [];
    const out: number[] = [];
    for (let p: RentalPrescription | undefined = e.p; p && !out.includes(p.id); p = list.find((x) => x.id === p!.replaces)) out.push(p.id);
    return out;
  }

  private keyOf(lineId: number, id: number): string | null {
    const p = this.store.line(lineId)?.decisions?.prescriptions.find((x) => x.id === id);
    return p ? entryKey(p) : null;
  }

  private duration(): number | null {
    const t = this.durationText.trim();
    if (!t) return null;
    const n = Number(t);
    return Number.isInteger(n) && n > 0 ? n : null;
  }

  private errorsOf(): string[] {
    const out: string[] = [];
    if (this.start && !this.selected.length) out.push(NO_LINE);
    if (this.start && this.fileRequired) out.push(NO_FILE);
    if (this.start && !this.prescribedOn) out.push(NO_DATE); // date et prescripteur figurent sur toute ordonnance (JP 28/10)
    if (this.start && !this.reference.trim()) out.push(NO_PRESCRIBER);
    if (this.endMode === 'end' && this.start && this.end && this.end < this.start) out.push('Le dernier jour couvert précède le premier jour prescrit.');
    if (this.endMode === 'duration' && this.durationText.trim() && this.duration() === null) out.push('La durée est un nombre entier, au moins 1.');
    if (this.replacing && !this.replaceReason.trim()) out.push(REPLACE_REASON);
    return out;
  }

  // Un évènement par matériel coché : remplacement de son entrée si l'ordonnance remplacée le couvrait, ajout sinon.
  private events(): RentalDecisionEvent[] {
    if (!this.start || !this.selected.length) return [];
    let end: PrescriptionEnd;
    if (this.endMode === 'end') {
      if (!this.end) return [];
      end = { end: this.end };
    } else {
      const n = this.duration();
      if (n === null) return [];
      end = { duration: this.unit === 'YEAR' ? { value: n * 12, unit: 'MONTH' } : { value: n, unit: this.unit } };
    }
    const v = { start: this.start, end, prescribed_on: this.prescribedOn, reference: this.reference };
    return this.selected.map((lineId) => {
      const id = this.replacing?.get(lineId);
      return id != null ? prescriptionReplace(this.data.orderId, lineId, id, this.replaceReason, v) : prescriptionAdd(this.data.orderId, lineId, v);
    });
  }

  // Case de tête : tout ou une partie coché → tout décoché ; rien → tout coché.
  toggleAll(box: HTMLInputElement): void {
    this.selected = this.selected.length ? [] : this.candidates.map((c) => c.id);
    this.update();
    box.checked = this.allOn; // la liaison ne réécrit pas le DOM quand la valeur liée n'a pas changé
    box.indeterminate = this.someOn;
  }

  setTarget(t: 'ordonnance' | 'start'): void {
    if (this.pickTarget === t) return;
    this.pickTarget = t;
    this.cdr.markForCheck();
  }

  // Clic dans le calendrier avec la date de l'ordonnance pour cible : la date est posée, la cible passe au premier jour prescrit.
  pickPrescribedOn(d: Day): void {
    this.prescribedOn = d;
    this.pickTarget = 'start';
    this.update();
  }

  // Flèche : le premier jour prescrit reprend la date de l'ordonnance.
  prescribedToStart(): void {
    if (!this.prescribedOn) return;
    this.start = this.prescribedOn;
    this.leftMonth = monthShowing(this.leftMonth, this.start, this.months);
    this.pickTarget = 'start';
    this.update();
  }

  toggleLine(id: number, on: boolean): void {
    this.selected = on ? [...this.selected, id] : this.selected.filter((x) => x !== id);
    this.update();
  }

  // Remplacer part de l'ordonnance existante : mêmes dates, mêmes matériels cochés ; l'utilisateur ne corrige que ce qui change.
  useReplace(key: string | null): void {
    const r = key !== null ? this.rowsOf(prescriptionCandidates(this.store.rentalLines(), this.data.lineId)).find((x) => x.key === key && x.live) : undefined;
    if (!r) return;
    const p = r.entries[0].p;
    this.replaceKey = r.key;
    this.replacing = new Map(r.entries.map((e) => [e.lineId, e.p.id]));
    this.selected = r.entries.map((e) => e.lineId);
    this.start = p.start;
    this.end = p.end;
    this.endMode = 'end';
    this.durationText = '';
    this.prescribedOn = p.prescribed_on ?? null;
    this.reference = p.reference ?? '';
    this.replaceReason = '';
    this.leftMonth = monthShowing(this.leftMonth, p.start, this.months);
    this.pickPhase = 'end';
  }

  replace(key: string): void {
    this.useReplace(key);
    this.update();
    this.host.nativeElement.querySelector('.dlg-body')?.scrollTo({ top: 0 }); // la saisie est en tête, l'historique dessous
  }

  private stopReplace(): void {
    this.replaceKey = null;
    this.replacing = null;
  }

  newPrescription(): void {
    this.stopReplace();
    this.start = null;
    this.end = null;
    this.endMode = 'duration'; // un remplacement passe en dernier jour ; la saisie neuve revient au défaut
    this.durationText = '';
    this.prescribedOn = null;
    this.reference = '';
    this.replaceReason = '';
    this.pickPhase = null;
    this.update();
  }

  onPickPhase(p: PickerPhase): void {
    if (this.pickPhase !== null) this.pickPhase = p;
  }

  setEndMode(m: 'end' | 'duration'): void {
    this.endMode = m;
    this.update();
  }

  setStart(d: Day | null): void {
    this.start = d;
    this.update();
  }

  setEnd(d: Day | null): void {
    this.end = d;
    this.update();
  }

  typedStart(ev: Event): void {
    const v = this.typedDay(ev, this.leftMonth);
    if (v === undefined) return;
    this.start = v;
    if (v) this.leftMonth = monthShowing(this.leftMonth, v, this.months);
    this.update();
  }

  typedEnd(ev: Event): void {
    const v = this.typedDay(ev, this.leftMonth);
    if (v === undefined) return;
    this.end = v;
    if (v) this.leftMonth = monthShowing(this.leftMonth, v, this.months);
    this.update();
  }

  typedPrescribedOn(ev: Event): void {
    const v = this.typedDay(ev, this.leftMonth);
    if (v === undefined) return;
    this.prescribedOn = v;
    this.update();
  }

  setDuration(v: string): void {
    this.durationText = v;
    this.update();
  }

  setUnit(u: Unit): void {
    this.unit = u;
    this.update();
  }

  setReference(v: string): void {
    this.reference = v;
    this.update();
  }

  setReplaceReason(v: string): void {
    this.replaceReason = v;
    this.update();
  }

  setLeftMonth(d: Day): void {
    this.leftMonth = d;
  }

  // Annuler une ordonnance : toutes ses entrées cochées (une par matériel), décochables dans le dialogue d'annulation.
  cancel(key: string): void {
    const r = this.rows.find((x) => x.key === key && x.live);
    if (!r) return;
    const p = r.entries[0].p;
    this.nested(this.dialogs.openCancelDecision({
      store: this.store, orderId: this.data.orderId, typ: EventType.RENTAL_PRESCRIPTION,
      targets: r.entries.map((e) => ({ lineId: e.lineId, id: e.p.id, checked: true })),
      title: 'Annuler la prescription',
      summary: `Prescription du ${frDay(p.start)} au ${frDay(p.end)} inclus · ${r.lines}`,
    }));
  }

  save(): void {
    const evs = this.canSave ? this.events() : [];
    if (!evs.length) return;
    const files = [...this.pending];
    const meta = { prescribedOn: this.prescribedOn, prescriber: this.reference, lineIds: [...this.selected] };
    this.send(evs, 'Prescription', 'f', files.length ? (run) => this.upload(files, fileRefsOf(run), meta) : undefined);
  }

  // --- Fichiers d'ordonnance ---

  pickPending(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    this.pending = [...this.pending, ...Array.from(input.files ?? [])];
    input.value = '';
    this.update();
    this.cdr.markForCheck();
  }

  // Vérifier le fichier choisi avant l'envoi (JP 28/10) : PDF et images s'ouvrent dans un onglet, le reste se télécharge (une URL blob s'exécuterait dans l'origine du front).
  previewPending(i: number): void {
    const file = this.pending[i];
    if (!file) return;
    const url = URL.createObjectURL(file);
    if (file.type && canOpenInline(file.type)) window.open(url, '_blank');
    else Object.assign(document.createElement('a'), { href: url, download: file.name }).click();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }

  removePending(i: number): void {
    this.pending = this.pending.filter((_, k) => k !== i);
    this.update();
  }

  attachTo(key: string, ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    const r = this.rows.find((x) => x.key === key);
    if (!files.length || !r || this.attaching !== null) return;
    this.attaching = key;
    this.cdr.markForCheck();
    const p = r.entries[0].p;
    this.upload(files, r.entries.map((e) => ({ line_id: e.lineId, id: e.p.id })), { prescribedOn: p.prescribed_on ?? null, prescriber: p.reference ?? null, lineIds: r.entries.map((e) => e.lineId) }, r.files.length).subscribe({ complete: () => { this.attaching = null; this.loadFiles(); } });
  }

  openFile(a: Attach): void {
    if (a.id) this.attach.openInNewTab(a.id);
  }

  // En série (le serveur numérote les pièces par MAX(seq) + 1) ; un refus ou une panne est dit, les autres fichiers partent quand même.
  // Nom que prendra le i-ème fichier en attente, affiché avant l'enregistrement.
  pendingName(i: number): string {
    return prescriptionFileName(this.nameParts({ prescribedOn: this.prescribedOn, prescriber: this.reference, lineIds: this.selected }), this.pending[i]?.name ?? '', i + 1);
  }

  // Patient = mémo du premier matériel prescrit, sinon le bénéficiaire du dossier ; jamais le client (un établissement).
  private nameParts(m: { prescribedOn: Day | null; prescriber: string | null; lineIds: number[] }) {
    const memo = m.lineIds.map((id) => this.store.line(id)?.memo?.trim()).find((x) => !!x);
    return { patient: memo || this.store.beneficiary?.trim() || null, orderId: this.data.orderId, prescribedOn: m.prescribedOn, prescriber: m.prescriber };
  }

  // already : fichiers déjà joints à cette ordonnance, pour poursuivre la numérotation _2, _3…
  private upload(files: File[], refs: PrescriptionFileRef[], meta: { prescribedOn: Day | null; prescriber: string | null; lineIds: number[] }, already = 0): Observable<unknown> {
    const named = files.map((f, i) => new File([f], prescriptionFileName(this.nameParts(meta), f.name, already + i + 1), { type: f.type }));
    return from(named).pipe(
      concatMap((file) => this.svc.attachPrescriptionFile(file, this.data.orderId, refs).pipe(
        tap((r) => { if (r?.success === false) this.alert.error(`${file.name} non joint : ${r.error_msg ?? r.msg ?? 'refusé'}`); }),
        catchError(() => { this.alert.error(`${file.name} non joint : envoi impossible`); return of(null); }),
      )),
      finalize(() => this.store.filesAttached()),
    );
  }

  // Échec de lecture dit, avec [Réessayer] : sans la liste, une ordonnance semblerait n'avoir aucun fichier.
  loadFiles(): void {
    this.filesError = null;
    this.svc.getPrescriptionFiles(this.data.orderId).subscribe({
      next: (all) => {
        this.files = all;
        this.update();
        this.cdr.markForCheck();
      },
      error: (e) => {
        this.filesError = `Fichiers d'ordonnance illisibles : ${serverMessage(e)}`;
        this.cdr.markForCheck();
      },
    });
  }
}
